const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5215';

// Log API configuration on module load
console.log('[FeatureFlagsAPI] Initializing Feature Flags API service');
console.log('[FeatureFlagsAPI] API_BASE_URL:', API_BASE_URL);

const makeRequest = async (url, options = {}) => {
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  try {
    console.log(`[FeatureFlagsAPI][${requestId}] Making request`, {
      url,
      method: options.method || 'GET',
      apiBaseUrl: API_BASE_URL
    });

    const startTime = performance.now();
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    const endTime = performance.now();
    const duration = (endTime - startTime).toFixed(2);

    console.log(`[FeatureFlagsAPI][${requestId}] Response received`, {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      duration: `${duration}ms`,
    });
    
    if (!response.ok) {
      let errorDetails = null;
      try {
        const errorBody = await response.clone().json();
        errorDetails = errorBody;
        console.error(`[FeatureFlagsAPI][${requestId}] Error response body:`, errorBody);
      } catch (e) {
        const errorText = await response.clone().text();
        errorDetails = errorText;
        console.error(`[FeatureFlagsAPI][${requestId}] Error response text:`, errorText);
      }

      const error = new Error(`HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.details = errorDetails;
      throw error;
    }
    
    const data = await response.json();
    console.log(`[FeatureFlagsAPI][${requestId}] Successfully completed request`);
    return data;
  } catch (error) {
    console.error(`[FeatureFlagsAPI][${requestId}] Error in request:`, {
      name: error.name,
      message: error.message,
      status: error.status,
      statusText: error.statusText,
      details: error.details,
    });

    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error(`[FeatureFlagsAPI][${requestId}] Network error detected`);
    }

    throw error;
  }
};

export const fetchAllFeatureFlags = async () => {
  const url = `${API_BASE_URL}/api/featureflags`;
  return await makeRequest(url);
};

export const fetchFeatureFlag = async (id) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(id)}`;
  return await makeRequest(url);
};

export const addTenant = async (flagId, tenant) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(flagId)}/tenants`;
  return await makeRequest(url, {
    method: 'PATCH',
    body: JSON.stringify({
      action: 'add',
      tenant: tenant,
    }),
  });
};

export const removeTenant = async (flagId, tenant) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(flagId)}/tenants`;
  return await makeRequest(url, {
    method: 'PATCH',
    body: JSON.stringify({
      action: 'remove',
      tenant: tenant,
    }),
  });
};

export const searchUsers = async (tenant, email) => {
  const url = `${API_BASE_URL}/api/users/search?tenant=${encodeURIComponent(tenant)}&email=${encodeURIComponent(email)}`;
  return await makeRequest(url);
};

export const getUsersByIds = async (tenant, userIds) => {
  const url = `${API_BASE_URL}/api/users/by-ids?tenant=${encodeURIComponent(tenant)}`;
  return await makeRequest(url, {
    method: 'POST',
    body: JSON.stringify(userIds),
  });
};

export const addUser = async (flagId, tenant, userIdOrEmail) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(flagId)}/users`;
  
  // Check if it's a GUID (userId) or email
  const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userIdOrEmail);
  
  const requestBody = {
    action: 'add',
    tenant: tenant,
  };
  
  if (isGuid) {
    requestBody.userId = userIdOrEmail;
  } else {
    requestBody.email = userIdOrEmail;
  }
  
  return await makeRequest(url, {
    method: 'PATCH',
    body: JSON.stringify(requestBody),
  });
};

export const removeUser = async (flagId, tenant, userId) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(flagId)}/users`;
  return await makeRequest(url, {
    method: 'PATCH',
    body: JSON.stringify({
      action: 'remove',
      tenant: tenant,
      userId: userId,
    }),
  });
};

export const updateEnabled = async (flagId, enabled) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(flagId)}/enabled`;
  return await makeRequest(url, {
    method: 'PATCH',
    body: JSON.stringify({
      enabled: enabled,
    }),
  });
};

export const updateDescription = async (flagId, description) => {
  const url = `${API_BASE_URL}/api/featureflags/${encodeURIComponent(flagId)}/description`;
  return await makeRequest(url, {
    method: 'PATCH',
    body: JSON.stringify({
      description: description,
    }),
  });
};
